"use client"

import React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import { router } from "@inertiajs/react"
import { FilePlus, Minus, Plus, Trash2 } from "lucide-react"
import "./Node.css"
import "./CreateNoteNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))
const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const NOTE_CATEGORY_ENDPOINT = "https://api.integrityprodserver.com/intserver-api/lp/getNoteCategory.php"
const DEFAULT_NOTE_CATEGORIES = [
  { nct_id: "1", note_category: "General" },
  { nct_id: "2", note_category: "Installer App" },
  { nct_id: "3", note_category: "Product Description" },
  { nct_id: "4", note_category: "Technician Debrief" },
]

const VAR_HINT = "Tip: use {{variableName}} to pull values from previous steps."

const CreateNoteNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_NOTE_CATEGORIES)
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)
  const [categoryLoadError, setCategoryLoadError] = useState("")
  const nodes = useNodes()
  const edges = useEdges()

  const buildState = (src, srcSettings) => ({
    label: ensureString(src.label) || "Create Note",
    name: ensureString(src.name) || ensureString(src.label) || "Create Note",
    description: ensureString(src.description),
    mode: ensureString(src.mode) || getSettingValue(srcSettings, "mode", "job") || "job",
    recid: ensureString(src.recid) || getSettingValue(srcSettings, "recid", ""),
    noteText: ensureString(src.noteText) || getSettingValue(srcSettings, "note_notes", ""),
    categorySource: ensureString(src.categorySource) || getSettingValue(srcSettings, "category_source", "label") || "label",
    noteCategory: ensureString(src.noteCategory) || getSettingValue(srcSettings, "note_category", "General") || "General",
    nctId: ensureString(src.nctId) || getSettingValue(srcSettings, "nct_id", ""),
    noteImportant:
      typeof src.noteImportant === "boolean"
        ? src.noteImportant
        : Boolean(srcSettings?.note_important),
    expanded: src.expanded ?? true,
  })

  const [nodeData, setNodeData] = useState(() => buildState(data, settings))
  const nodeDataRef = useRef(nodeData)
  const lastAutoSyncKeyRef = useRef("")

  useEffect(() => {
    setNodeData(buildState(data, data?.settings || {}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.label, data.name, data.description, data.mode, data.recid,
    data.noteText, data.categorySource, data.noteCategory, data.nctId,
    data.noteImportant, data.expanded, data.settings, id,
  ])

  useEffect(() => {
    nodeDataRef.current = nodeData
  }, [nodeData])

  // Resolve the persisted database id for this step. Once we have it the node
  // is "live" and update/delete via Inertia is enabled.
  const stepDatabaseId =
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)

  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  // --- Category loader (same pattern as UpdateNoteNode) --------------------
  useEffect(() => {
    const abortController = new AbortController()

    const loadCategories = async () => {
      setIsLoadingCategories(true)
      setCategoryLoadError("")

      try {
        const response = await fetch(NOTE_CATEGORY_ENDPOINT, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Category request failed with status ${response.status}`)
        }

        const payload = await response.json()
        const fetchedCategories = Array.isArray(payload?.categories) ? payload.categories : []
        const normalized = fetchedCategories
          .map((item) => ({
            nct_id: ensureString(item?.nct_id),
            note_category: ensureString(item?.note_category),
          }))
          .filter((item) => item.note_category !== "")

        if (normalized.length > 0) {
          setCategoryOptions(normalized)
        } else {
          setCategoryLoadError("No note categories returned from LP API. Using defaults.")
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load LP note categories:", error)
          setCategoryLoadError("Unable to load categories from LP API. Using defaults.")
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingCategories(false)
        }
      }
    }

    loadCategories()

    return () => {
      abortController.abort()
    }
  }, [])

  // --- nctId auto-sync from selected label ---------------------------------
  // Backend prefers numeric nct_id over note_category labels (LP rejects
  // labels like "A/R Workflow"). Whenever a label is selected and we now
  // know the matching nct_id, persist it.
  useEffect(() => {
    if (nodeData.categorySource === "nct_id") return
    if (!nodeData.noteCategory || nodeData.nctId) return
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return

    const matched = categoryOptions.find((c) => c.note_category === nodeData.noteCategory)
    const resolvedNctId = ensureString(matched?.nct_id)
    if (!resolvedNctId) return
    const autoSyncKey = `${nodeData.noteCategory}::${resolvedNctId}`
    if (lastAutoSyncKeyRef.current === autoSyncKey) return

    const updated = {
      ...nodeDataRef.current,
      nctId: resolvedNctId,
    }

    lastAutoSyncKeyRef.current = autoSyncKey
    setNodeData(updated)
    updateWorkflowContext(updated)
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...updated, expanded })
    }
  }, [
    categoryOptions,
    nodeData.categorySource,
    nodeData.noteCategory,
    nodeData.nctId,
    updateWorkflowContext,
    data,
    id,
    expanded,
  ])

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...nodeDataRef.current,
      [field]: typeof value === "boolean" ? value : ensureString(value),
    }
    if (field === "name") {
      updated.label = ensureString(value) || "Create Note"
    }
    setNodeData(updated)
    if (persistOnChange) {
      updateWorkflowContext(updated)
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...updated, expanded })
      }
    }
  }

  const handleCategorySelectChange = (selectedCategory) => {
    const matched = categoryOptions.find((c) => c.note_category === selectedCategory)
    const updated = {
      ...nodeDataRef.current,
      noteCategory: ensureString(selectedCategory),
      nctId: ensureString(matched?.nct_id || nodeDataRef.current.nctId || ""),
    }
    setNodeData(updated)
    updateWorkflowContext(updated)
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...updated, expanded })
    }
  }

  const handleBlur = () => {
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = nodeDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) {
        persist(nodeId, { ...current, expanded })
      }
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this node?")) {
      return
    }

    if (!currentWorkflow?.id || !stepDatabaseId) {
      console.error("Create Note node: cannot delete -- missing workflow or step database id")
      return
    }

    router.delete(`/workflows/${currentWorkflow.id}/steps/${stepDatabaseId}`, {
      preserveState: true,
      preserveScroll: true,
      only: [],
      onSuccess: () => {
        if (typeof data.onDeleteNode === "function") {
          data.onDeleteNode(id)
        }
      },
      onError: (errors) => {
        console.error("Create Note node: failed to delete step via Inertia", errors)
        alert("Failed to delete node. Please try again.")
      },
    })
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".create-note-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  const inputClass =
    "w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-emerald-500 nodrag"

  const getAvailableRequestVariables = () => {
    if (!nodes || !edges) return []
    const availableVariables = []
    const visitedNodes = new Set()

    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (
        (node.type === "requestNode" || node.type === "getNoteNode") &&
        Array.isArray(node.data?.extractedVariables)
      ) {
        availableVariables.push(...node.data.extractedVariables)
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId)
      incomingEdges.forEach((edge) => findUpstreamRequestNodes(edge.source))
    }

    findUpstreamRequestNodes(id)
    return availableVariables.filter((variable) => variable && typeof variable.name === "string")
  }

  const getAvailableFormFields = () => collectUpstreamFormFieldVariables(nodes, edges, id)

  const getSystemVariables = () =>
    getSystemVariablesForContext({ hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id) })

  const getAvailableVariables = () => {
    const requestVariables = getAvailableRequestVariables()
    const formVariables = getAvailableFormFields()
    return [...requestVariables, ...formVariables]
  }

  return (
    <div
      className={`trigger-node-container create-note-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "create-note-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <FilePlus size={16} className="trigger-icon" />
          <span>{nodeData.name && nodeData.name.trim() !== "" ? nodeData.name : "Create Note"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="createNoteNode"
            nodeName={nodeData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={(e) => {
              e.stopPropagation()
              handleDelete()
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="create-note-info-banner">
            <strong>Create Note</strong> &mdash; calls LP&apos;s <code>saveNote.php</code> to insert a brand-new note in <code>not_notes</code>.
            <div style={{ marginTop: 4 }}>
              <code>note_id</code> is intentionally omitted, which tells LP to insert a new row instead of updating an existing one.
            </div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Node Name</label>
            <EnhancedVariableReference
              value={nodeData.name}
              onChange={(value) => handleChange("name", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Create Note"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Description</label>
            <EnhancedVariableReference
              value={nodeData.description}
              onChange={(value) => handleChange("description", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Create a new LP note"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Mode<span className="create-note-required">*</span>
            </label>
            <select
              value={nodeData.mode}
              onChange={(e) => handleChange("mode", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className={inputClass}
            >
              <option value="job">Job (job note)</option>
              <option value="cst">Customer (cst note)</option>
            </select>
            <div className="create-note-field-hint">
              Picks which not_notes record set to insert into. Most workflows use <strong>job</strong>.
            </div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Record ID (recid)<span className="create-note-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.recid}
              onChange={(value) => handleChange("recid", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="{{recid}}"
              className={inputClass}
              showDateVariables={true}
            />
            <div className="create-note-field-hint">{VAR_HINT}</div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Note Text<span className="create-note-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.noteText}
              onChange={(value) => handleChange("noteText", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              multiline={true}
              rows={3}
              placeholder="Body of the new note"
              className={inputClass}
              showDateVariables={true}
            />
            <div className="create-note-field-hint">{VAR_HINT}</div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Category Source</label>
            <div className="create-note-radio-row">
              <label>
                <input
                  type="radio"
                  name={`create-note-category-source-${id}`}
                  value="label"
                  checked={nodeData.categorySource !== "nct_id"}
                  onChange={() => handleChange("categorySource", "label", { persistOnChange: true })}
                />
                By label
              </label>
              <label>
                <input
                  type="radio"
                  name={`create-note-category-source-${id}`}
                  value="nct_id"
                  checked={nodeData.categorySource === "nct_id"}
                  onChange={() => handleChange("categorySource", "nct_id", { persistOnChange: true })}
                />
                By numeric ID (nct_id)
              </label>
            </div>
          </div>

          {nodeData.categorySource === "nct_id" ? (
            <div className="node-field">
              <label className="dark:text-white">
                Category ID (nct_id)<span className="create-note-required">*</span>
              </label>
              <EnhancedVariableReference
                value={nodeData.nctId}
                onChange={(value) => handleChange("nctId", value)}
                onBlur={handleBlur}
                availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                placeholder="e.g. 3"
                className={inputClass}
                showDateVariables={true}
              />
              <div className="create-note-field-hint">{VAR_HINT}</div>
            </div>
          ) : (
            <div className="node-field">
              <label className="dark:text-white">
                Category<span className="create-note-required">*</span>
              </label>
              <select
                value={nodeData.noteCategory}
                onChange={(e) => handleCategorySelectChange(e.target.value)}
                onBlur={handleBlur}
                className={inputClass}
              >
                {categoryOptions.map((category) => (
                  <option key={`${category.nct_id}-${category.note_category}`} value={category.note_category}>
                    {category.note_category}
                  </option>
                ))}
              </select>
              {isLoadingCategories && (
                <div className="create-note-field-hint">Loading categories from LP API...</div>
              )}
              {categoryLoadError && (
                <div className="create-note-field-hint">{categoryLoadError}</div>
              )}
            </div>
          )}

          <div className="node-field create-note-checkbox-row">
            <input
              id={`create-note-important-${id}`}
              type="checkbox"
              checked={Boolean(nodeData.noteImportant)}
              onChange={(e) => handleChange("noteImportant", e.target.checked, { persistOnChange: true })}
              className="nodrag"
            />
            <label
              htmlFor={`create-note-important-${id}`}
              className="dark:text-white"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              Mark as important
            </label>
          </div>
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default CreateNoteNode
